<script setup>
import {
	ref,
	onBeforeMount,
	watchEffect,
	computed,
	onMounted,
	onBeforeUnmount,
	watch,
} from 'vue';

import ParsingItem from './ParsingItem.vue';
import {
	Dropdown,
	DropdownItem,
	DropdownDivider,
} from '@/views/common/components/dropdown';

import useCrud from '@/composables/app/useCrud';
import useEnv from '@/composables/app/useEnv';
import useAuth from '@/composables/auth/useAuth';
import useCommonUtils from '@/composables/app/useCommonUtils';
import useToast from '@/composables/app/useToast';

import emitter from 'tiny-emitter/instance';
import { v4 } from 'uuid';

const { getData: getDataFromDbRequest } = useCrud(
	'/customer/upload-bulk-attachment/'
);
const {
	createData: markParsingAsComplete,
	postRequestLoading: markingAsComplete,
} = useCrud('/customer/bulk-attachment-mark-as-completed/');
const { createData: cancelParsing } = useCrud('/customer/cancel-parsing/');

const { websocketURL } = useEnv();
const { authMember } = useAuth();
const { isJsonParsable } = useCommonUtils();
const { showDefaultToast, showConfirmModal } = useToast();

const minimized = ref(true);
const show = ref(false);

const filesFromDb = ref([]);
const uploadedFiles = ref([]);
const lastSocketResponse = ref(null);
const responsesFromSocket = ref([]);
const responsesFromAction = ref([]);

watchEffect(() => {
	show.value = Boolean(
		uploadedFiles.value?.length ||
			filesFromDb.value?.length ||
			responsesFromSocket.value?.length
	);
});

const getDataFromDb = async () => {
	const response = await getDataFromDbRequest();
	// filesFromDb.value = response?.length ? response : [];
	filesFromDb.value = response?.length
		? response.map(r => ({ ...r, origin: 'db', timestamp: Date.now() }))
		: [];
};

onBeforeMount(() => {
	getDataFromDb();
});
emitter.on('bulk-blue-color-files-uploaded', files => {
	minimized.value = false;
	uploadedFiles.value = files;
});

const socket = ref(null);
const socketUrl = computed(() => {
	const baseUrl = websocketURL.includes('/notifications')
		? websocketURL.replace('/notifications', '')
		: websocketURL;
	return `${baseUrl}/attachment_update/${authMember.value?.id}/`;
});

const onSocketOpen = () => {
	socket.value = new WebSocket(socketUrl.value);
	if (socket.value) {
		socket.value.onmessage = payload => {
			let payloadData = isJsonParsable(payload?.data)
				? JSON.parse(payload?.data)
				: {};

			lastSocketResponse.value = payloadData?.message
				? {
						id: `socket-${v4()}`,
						...payloadData?.message,
						origin: 'socket',
						timestamp: Date.now(),
				  }
				: {};

			responsesFromSocket.value = [lastSocketResponse.value].concat(
				responsesFromSocket.value
			);
		};
	}
};
const onSocketClose = () => {
	if (!socket.value) return;
	socket.value.close();
};
onMounted(() => {
	onSocketOpen();
	// window.addEventListener('beforeunload', browserCloseDialogue, false);
});
onBeforeUnmount(() => {
	onSocketClose();
	// window.removeEventListener('beforeunload', browserCloseDialogue, false);
});

const filterOptions = computed(() => {
	return [
		{ id: v4(), label: 'All', value: 'all', appendDivider: true },
		{ id: v4(), label: 'Pending', value: 'pending', appendDivider: true },
		{
			id: v4(),
			label: 'Not found',
			value: 'not-found',
			appendDivider: false,
		},
		{
			id: v4(),
			label: 'Not matched',
			value: 'not-matched',
			appendDivider: true,
		},
		{ id: v4(), label: 'Completed', value: 'completed', appendDivider: false },
	];
});
const activeFilter = ref(filterOptions.value.find(f => f?.value === 'all'));

const hasStatus = (file, status) => {
	if (typeof status === 'string') status = [status];
	return Boolean(status?.includes(file?.status));
};
const isCompleted = file => {
	return Boolean(
		!file?.status || ['success', 'completed'].includes(file?.status)
	);
};

const filterFiles = files => {
	switch (activeFilter.value?.value) {
		case 'all':
			return files;

		case 'pending':
			return files.filter(f => hasStatus(f, ['pending']));

		case 'not-found':
			return files.filter(f => hasStatus(f, ['not_found', 'not-found']));

		case 'not-matched':
			return files.filter(f => hasStatus(f, ['not_matched', 'not-matched']));

		case 'completed':
			return files.filter(f => isCompleted(f));

		default:
			return files;
	}
};

const fileList = computed(() => {
	const allFiles = [
		...filesFromDb.value,
		...responsesFromSocket.value,
		...uploadedFiles.value,
		...responsesFromAction.value,
	];

	const allFileIds = allFiles
		.filter(file => file?.file_id)
		.map(file => file?.file_id);

	const uniqueFileIds = allFileIds.filter(
		(id, i, files) => files.indexOf(id) === i
	);

	const files = uniqueFileIds.map(fileId => {
		const filesById = allFiles.filter(file => file?.file_id === fileId);
		if (filesById?.length === 1) return filesById[0];
		else {
			const sortedFilesByTimestamp = filesById.sort((a, b) =>
				Number(a?.timestamp) >= Number(b?.timestamp) ? -1 : 1
			);
			return sortedFilesByTimestamp[0];
		}
	});

	return files;
});

const filteredFiles = computed(() => filterFiles(fileList.value));

const onMarkUploadAsComplete = async () => {
	const response = await markParsingAsComplete({});
	if (response?.code == 0) {
		uploadedFiles.value = [];
		responsesFromAction.value = [];
		responsesFromSocket.value = [];
		await getDataFromDb();

		showDefaultToast(
			'Success',
			'success',
			response?.message
				? response?.message
				: 'Data has been reset successfully...'
		);
	} else {
		showDefaultToast(
			'Error',
			'danger',
			response?.message ? response?.message : 'Parsing data not reset...'
		);
	}
};

const isUploadCompleted = computed(() => {
	if (!fileList.value?.length) return false;
	return fileList.value.every(file => !hasStatus(file, 'pending'));
});

const uploadStatus = computed(() => {
	const countFiles = fileList.value?.length;
	const countPending = fileList.value.filter(file =>
		hasStatus(file, ['pending'])
	)?.length;
	const countCompleted = fileList.value.filter(file =>
		isCompleted(file)
	)?.length;
	const countNotFound = fileList.value.filter(file =>
		hasStatus(file, ['not_found', 'not-found'])
	)?.length;
	const countNotMatched = fileList.value.filter(file =>
		hasStatus(file, ['not_matched', 'not-matched'])
	)?.length;
	let msg = `Out of ${countFiles} files: ${countCompleted} uploaded, ${countPending} pending${
		countNotFound ? `, ${countNotFound} not found` : ''
	}${countNotMatched ? `, ${countNotMatched} not matched` : ''}`;

	return msg;
});

watch(
	() => isUploadCompleted.value,
	(val, oldVal) => {
		if (!responsesFromSocket.value?.length) return false;
		if (!oldVal && val) {
			emitter.emit('reload-candidate-list');
		}
	}
);

// const onCancelParsing = () => {
// 	showConfirmModal({ message: 'Are you sure to cancel?' }, async confirmed => {
// 		if (!confirmed) return;

// 		const response = await cancelParsing();
// 		if (response?.code == 0) {
// 			showDefaultToast('Success', 'success', 'Parsing has been canceled');
// 			filesFromDb.value = [];
// 			responsesFromSocket.value = [];
// 			uploadedFiles.value = [];
// 			responsesFromAction.value = [];
// 		}
// 	});
// };
</script>

<template>
	<Teleport to="body">
		<div
			v-if="fileList?.length"
			class="bulk-parsing-container shadow-small bg-card d-flex flex-column"
		>
			<div
				class="w-full flex-between parsing-header bg-color-18 dark-bg-color-19"
			>
				<div>
					<div class="fw-bold text-color-2">Bulk file upload status</div>
					<div class="text-11px">{{ uploadStatus }}</div>
				</div>

				<div class="flex-start">
					<FeatherIcon
						:type="minimized ? 'maximize-2' : 'minimize-2'"
						size="20"
						class="cursor-pointer"
						@click="minimized = !minimized"
					/>
				</div>
			</div>

			<div
				v-if="!minimized"
				class="p-3 parsing-body overflow-auto flex-1 border"
			>
				<div class="w-100 flex-between mb-3">
					<div>
						<Dropdown min-width="196px">
							<template #trigger>
								<div class="flex-start">
									<span>{{ activeFilter?.label }}</span>
									<FeatherIcon type="chevron-down" class="ms-2" />
								</div>
							</template>

							<div>
								<template
									v-for="filterOption in filterOptions"
									:key="filterOption.id"
								>
									<DropdownItem @click="activeFilter = filterOption">{{
										filterOption?.label
									}}</DropdownItem>
									<DropdownDivider v-if="filterOption?.appendDivider" />
								</template>
							</div>
						</Dropdown>
					</div>

					<div class="flex-end ms-4">
						<!-- <button
							class="btn btn-danger flex-center font-bold text-18px cancel-btn"
							@click="onCancelParsing"
						>
							x
						</button> -->

						<LoadingButton
							v-if="isUploadCompleted"
							title="Mark Complete"
							variant="primary"
							class=""
							@submit="onMarkUploadAsComplete"
						/>
					</div>
				</div>

				<ParsingItem
					v-for="file in filteredFiles"
					:key="file?.file_id"
					:file="file"
					:has-status="hasStatus"
				/>
			</div>
		</div>
	</Teleport>
</template>

<style lang="scss" scoped>
.bulk-parsing-container {
	position: fixed;
	bottom: 0;
	right: 64px;
	width: 520px;
	max-width: calc(98% - 64px);
	max-height: calc(100% - 120px);
	border-top-left-radius: 6px;
	border-top-right-radius: 6px;
	z-index: 2777;

	.parsing-header {
		padding: 12px 16px;
		border-top-left-radius: 6px;
		border-top-right-radius: 6px;
		border-top: 1px solid #3b3f5c;
		border-left: 1px solid #3b3f5c;
		border-right: 1px solid #3b3f5c;
		// height: 52px;
	}

	.cancel-btn {
		width: 39px;
		height: 39px;
	}
}
</style>
